import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Play, Pause, RotateCcw, Volume2, VolumeX } from 'lucide-react';
const Index = () => {
  // CPR State - kombiniert Stoppuhr und Metronom
  const [time, setTime] = useState(0);
  const [cprRunning, setCprRunning] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const metronomeIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // CPR Funktionen - startet Stoppuhr und Metronom zusammen
  const startCPR = async () => {
    setCprRunning(true);

    // Stoppuhr starten
    intervalRef.current = setInterval(() => {
      setTime(prevTime => prevTime + 1);
    }, 10); // 10ms für Centisekunden

    // Metronom starten - AudioContext direkt erstellen
    let ctx = audioContext;
    if (!ctx) {
      ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      setAudioContext(ctx);
    }
    
    // AudioContext aktivieren falls suspended
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    
    const bpm = 110;
    const interval = 60000 / bpm; // Millisekunden zwischen Beats

    // Ersten Beat sofort spielen
    try {
      playBeep(ctx);
    } catch (error) {
      console.log('Audio playback failed:', error);
    }
    
    metronomeIntervalRef.current = setInterval(() => {
      try {
        playBeep(ctx!);
      } catch (error) {
        console.log('Audio playback failed:', error);
      }
    }, interval);
  };
  const stopCPR = () => {
    setCprRunning(false);

    // Stoppuhr stoppen
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Metronom stoppen
    if (metronomeIntervalRef.current) {
      clearInterval(metronomeIntervalRef.current);
    }
  };
  const resetCPR = () => {
    setCprRunning(false);
    setTime(0);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (metronomeIntervalRef.current) {
      clearInterval(metronomeIntervalRef.current);
    }
  };

  // Zeit formatieren
  const formatTime = (centiseconds: number) => {
    const minutes = Math.floor(centiseconds / 6000);
    const seconds = Math.floor(centiseconds % 6000 / 100);
    const cs = centiseconds % 100;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
  };

  // Metronom Funktionen
  const initAudioContext = () => {
    if (!audioContext) {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      setAudioContext(ctx);
      return ctx;
    }
    return audioContext;
  };
  const playBeep = (ctx: AudioContext) => {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.frequency.setValueAtTime(800, ctx.currentTime); // 800Hz Ton
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.1);
  };

  // Cleanup bei Unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (metronomeIntervalRef.current) clearInterval(metronomeIntervalRef.current);
      if (audioContext) audioContext.close();
    };
  }, [audioContext]);
  return <div className="min-h-screen bg-red-50 p-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-red-800 text-center mb-6">REANIMATIONS-HILFE</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* BLS AED Algorithmus */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-red-700">BLS AED Algorithmus</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-center">
                  <img src="./images/big_62bacd412503c4.77996195.jpg" alt="BLS AED Algorithmus" className="max-w-full h-auto rounded-lg shadow-lg" />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Stoppuhr und Metronom */}
          <div className="space-y-6">
            {/* Stoppuhr */}
            <Card>
              <CardHeader>
                <CardTitle className="text-red-700">CPR TIMER</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="text-4xl font-mono font-bold text-red-800 mb-6">
                  {formatTime(time)}
                </div>
                <div className="flex flex-col gap-3">
                  {!cprRunning ? <Button onClick={startCPR} size="lg" className="bg-green-600 hover:bg-green-700 w-full">
                      <Play className="mr-2 h-4 w-4" />
                      CPR STARTEN
                    </Button> : <Button onClick={stopCPR} size="lg" className="bg-red-600 hover:bg-red-700 w-full">
                      <Pause className="mr-2 h-4 w-4" />
                      CPR STOPPEN
                    </Button>}
                  <Button onClick={resetCPR} size="lg" variant="outline" className="w-full">
                    <RotateCcw className="mr-2 h-4 w-4" />
                    RESET
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Metronom */}
            <Card>
              <CardHeader>
                <CardTitle className="text-red-700">CPR-RHYTHMUS</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="text-3xl font-bold text-red-800 mb-3">
                  110 BPM
                </div>
                
                
                {cprRunning && <div className="mt-4">
                    <div className="w-6 h-6 bg-red-500 rounded-full mx-auto animate-pulse"></div>
                    <p className="text-sm text-gray-500 mt-2">Rhythmus aktiv</p>
                  </div>}
                
                {!cprRunning && <p className="text-sm text-gray-400 mt-4">
                    Wird automatisch mit CPR gestartet
                  </p>}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>;
};
export default Index;